import { Close, NotificationsActiveRounded, ScienceOutlined } from '@mui/icons-material';
import { Badge, Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';

import { useClassicNavigate } from '../hooks/useClassicNavigate';
import { dismissNotification } from '../services/notifications';
import { StyledIconButton } from '../theme/CustomComponents';
import { NotificationType } from '../Types/enums';
import { Notification } from '../Types/notifications';
import { formatDate } from '../util/dateUtils';
import { useNotifications } from './Providers/NotificationsProvider';

const NotificationTypeToIconMap: Record<NotificationType, ReactElement> = {
  [NotificationType.DEEP_RESEARCH_STARTED]: <ScienceOutlined color="info" />,
  [NotificationType.DEEP_RESEARCH_COMPLETED]: <ScienceOutlined color="success" />,
  [NotificationType.DEEP_RESEARCH_FAILED]: <ScienceOutlined color="error" />,
  [NotificationType.DEEP_RESEARCH_REFILL_REQUEST]: <ScienceOutlined color="info" />,
  [NotificationType.DEEP_RESEARCH_REFILL_APPROVED]: <ScienceOutlined color="success" />,
  [NotificationType.DEEP_RESEARCH_REFILL_DENIED]: <ScienceOutlined color="error" />,
  [NotificationType.APP_APPROVAL_REQUEST]: <NotificationsActiveRounded color="info" />,
  [NotificationType.APP_USER_APPROVAL_SENT]: <NotificationsActiveRounded color="info" />,
  [NotificationType.APP_USER_APPROVAL_APPROVED]: <NotificationsActiveRounded color="success" />,
  [NotificationType.APP_USER_APPROVAL_DENIED]: <NotificationsActiveRounded color="error" />,
  [NotificationType.FOLDER_SHARED]: <></>,
  [NotificationType.PROMPT_SHARED]: <></>,
  [NotificationType.INDEXING_COMPLETED]: <></>,
  [NotificationType.DISCOVERY_COMPLETED]: <></>,
  [NotificationType.ANALYSIS_COMPLETED]: <></>,
};

export const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hasNewNotification, setHasNewNotification] = useState(false);

  const {
    notifications,
    readNotifications,
    unreadNotifications,
    setReadNotifications,
    refetchNotifications,
  } = useNotifications();
  const navigate = useClassicNavigate();

  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDismiss = useCallback(
    async (notification: Notification) => {
      await dismissNotification(notification.id);
      refetchNotifications();
    },
    [refetchNotifications]
  );

  const handleMarkAsRead = useCallback(
    (id: string) => {
      setReadNotifications((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [setReadNotifications]
  );

  // Trigger ring animation when new notifications arrive
  useEffect(() => {
    if (unreadNotifications.length > 0) {
      setHasNewNotification(true);
      setTimeout(() => setHasNewNotification(false), 3000); // matches animation duration
    }
  }, [unreadNotifications]);

  return (
    <>
      <StyledIconButton color="inherit" onClick={handleOpen} size="large">
        <Badge badgeContent={unreadNotifications.length} color="error">
          <Box
            sx={{
              display: 'inline-block',
              animation: hasNewNotification ? 'ring 0.5s ease-in-out 0s 6' : 'none',
              '@keyframes ring': {
                '0%, 100%': { transform: 'rotate(0deg)' },
                '25%': { transform: 'rotate(15deg)' },
                '50%': { transform: 'rotate(-10deg)' },
                '75%': { transform: 'rotate(15deg)' },
              },
            }}
          >
            <NotificationsActiveRounded />
          </Box>
        </Badge>
      </StyledIconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { width: 400, maxHeight: 400, mt: 1 },
          },
        }}
      >
        {notifications?.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No notifications
            </Typography>
          </Box>
        )}

        {notifications?.map((notification) => {
          const isRead = readNotifications.includes(notification.id);
          return (
            <Tooltip
              title={notification.destinationUrl ? 'Go to related content' : ''}
              key={notification.id}
              disableInteractive
              arrow
            >
              <MenuItem
                onMouseEnter={() => handleMarkAsRead(notification.id)}
                onClick={() => {
                  if (notification.destinationUrl) {
                    navigate(notification.destinationUrl);
                    handleClose();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  whiteSpace: 'normal',
                  opacity: isRead ? 0.7 : 1,
                  cursor: notification.destinationUrl ? 'pointer' : 'default',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexGrow: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.2 }}>
                    {NotificationTypeToIconMap[notification.type as NotificationType]}
                  </Box>

                  <Box>
                    <Typography variant="body2">{notification.content}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(notification.createdAt.toString())}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Dismiss">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss(notification);
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            </Tooltip>
          );
        })}
      </Menu>
    </>
  );
};
